import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ComposedChart, Cell } from 'recharts';
const ExternalOEMAuditsDashboard: React.FC = () => {
  const kpis = [{
    title: 'Avg OEM Audit Score',
    value: '92/100',
    change: '+5 pts',
    isPositive: true,
    subtitle: 'Overall average'
  }, {
    title: 'Major Non-Conformances',
    value: '18',
    change: '-6',
    isPositive: true,
    subtitle: 'Active major NCRs'
  }, {
    title: 'Repeat Issues %',
    value: '12%',
    change: '-5%',
    isPositive: true,
    subtitle: 'Recurring findings'
  }];
  const oemAuditScoresData = [{
    oem: 'Tata Motors',
    q1: 88,
    q2: 90,
    q3: 92,
    latest: 92
  }, {
    oem: 'Bajaj Auto',
    q1: 90,
    q2: 91,
    q3: 93,
    latest: 93
  }, {
    oem: 'TVS Motor',
    q1: 89,
    q2: 91,
    q3: 92,
    latest: 92
  }, {
    oem: 'Hyundai',
    q1: 91,
    q2: 93,
    q3: 95,
    latest: 95
  }, {
    oem: 'Mahindra',
    q1: 87,
    q2: 89,
    q3: 90,
    latest: 90
  }, {
    oem: 'Hero MotoCorp',
    q1: 92,
    q2: 93,
    q3: 94,
    latest: 94
  }];
  const findingsSeverityData = [{
    category: 'Process Control',
    critical: 2,
    major: 5,
    minor: 12
  }, {
    category: 'Quality Systems',
    critical: 1,
    major: 4,
    minor: 8
  }, {
    category: 'Documentation',
    critical: 0,
    major: 3,
    minor: 15
  }, {
    category: 'Traceability',
    critical: 1,
    major: 6,
    minor: 10
  }, {
    category: 'Material Handling',
    critical: 0,
    major: 2,
    minor: 7
  }, {
    category: 'Calibration',
    critical: 0,
    major: 4,
    minor: 9
  }];
  const issuesRecurrenceData = [{
    month: 'Jan',
    repeatIssues: 8,
    totalIssues: 45,
    repeatPercent: 17.8
  }, {
    month: 'Feb',
    repeatIssues: 7,
    totalIssues: 42,
    repeatPercent: 16.7
  }, {
    month: 'Mar',
    repeatIssues: 6,
    totalIssues: 38,
    repeatPercent: 15.8
  }, {
    month: 'Apr',
    repeatIssues: 5,
    totalIssues: 35,
    repeatPercent: 14.3
  }, {
    month: 'May',
    repeatIssues: 5,
    totalIssues: 32,
    repeatPercent: 15.6
  }, {
    month: 'Jun',
    repeatIssues: 4,
    totalIssues: 28,
    repeatPercent: 14.3
  }, {
    month: 'Jul',
    repeatIssues: 4,
    totalIssues: 30,
    repeatPercent: 13.3
  }, {
    month: 'Aug',
    repeatIssues: 3,
    totalIssues: 26,
    repeatPercent: 11.5
  }];
  const oemAuditDetailsData = [{
    auditId: 'OEM-2024-001',
    oem: 'Tata Motors',
    plant: 'Manesar',
    auditDate: '2024-01-15',
    auditType: 'Supplier Quality Audit',
    auditor: 'Tata SQA Team',
    score: 92,
    criticalNCR: 0,
    majorNCR: 2,
    minorNCR: 5,
    repeatIssues: 1,
    capaOpen: 4,
    capaClosed: 3,
    nextAudit: '2024-07-15',
    status: 'Completed',
    remarks: 'Good performance, minor process documentation gaps'
  }, {
    auditId: 'OEM-2024-002',
    oem: 'Hyundai',
    plant: 'Chennai',
    auditDate: '2024-01-22',
    auditType: 'Annual Supplier Audit',
    auditor: 'Hyundai Quality Team',
    score: 95,
    criticalNCR: 0,
    majorNCR: 1,
    minorNCR: 3,
    repeatIssues: 0,
    capaOpen: 2,
    capaClosed: 2,
    nextAudit: '2025-01-22',
    status: 'Completed',
    remarks: 'Excellent audit results, best-in-class performance'
  }, {
    auditId: 'OEM-2024-003',
    oem: 'Bajaj Auto',
    plant: 'Pune',
    auditDate: '2024-02-05',
    auditType: 'Process Audit',
    auditor: 'Bajaj SQE Team',
    score: 93,
    criticalNCR: 0,
    majorNCR: 2,
    minorNCR: 4,
    repeatIssues: 1,
    capaOpen: 3,
    capaClosed: 3,
    nextAudit: '2024-08-05',
    status: 'Completed',
    remarks: 'Strong process controls, calibration records need update'
  }, {
    auditId: 'OEM-2024-004',
    oem: 'TVS Motor',
    plant: 'Bangalore',
    auditDate: '2024-02-12',
    auditType: 'Supplier Development Audit',
    auditor: 'TVS Quality Assurance',
    score: 92,
    criticalNCR: 1,
    majorNCR: 2,
    minorNCR: 6,
    repeatIssues: 2,
    capaOpen: 5,
    capaClosed: 4,
    nextAudit: '2024-08-12',
    status: 'In Progress',
    remarks: 'Critical NCR on traceability system - CAPA in progress'
  }, {
    auditId: 'OEM-2024-005',
    oem: 'Mahindra',
    plant: 'Manesar',
    auditDate: '2024-02-18',
    auditType: 'Quality System Audit',
    auditor: 'Mahindra QA Team',
    score: 90,
    criticalNCR: 0,
    majorNCR: 3,
    minorNCR: 7,
    repeatIssues: 1,
    capaOpen: 6,
    capaClosed: 4,
    nextAudit: '2024-08-18',
    status: 'In Progress',
    remarks: 'Process parameter monitoring gaps identified'
  }, {
    auditId: 'OEM-2024-006',
    oem: 'Hero MotoCorp',
    plant: 'Chennai',
    auditDate: '2024-02-25',
    auditType: 'Supplier Performance Audit',
    auditor: 'Hero Quality Team',
    score: 94,
    criticalNCR: 0,
    majorNCR: 1,
    minorNCR: 4,
    repeatIssues: 0,
    capaOpen: 3,
    capaClosed: 2,
    nextAudit: '2024-08-25',
    status: 'Completed',
    remarks: 'Very good performance, minor SPC improvements needed'
  }, {
    auditId: 'OEM-2024-007',
    oem: 'Tata Motors',
    plant: 'Pune',
    auditDate: '2024-03-05',
    auditType: 'Special Process Audit',
    auditor: 'Tata SQA Team',
    score: 91,
    criticalNCR: 0,
    majorNCR: 2,
    minorNCR: 6,
    repeatIssues: 1,
    capaOpen: 5,
    capaClosed: 3,
    nextAudit: '2024-09-05',
    status: 'In Progress',
    remarks: 'Heat treatment process controls require enhancement'
  }, {
    auditId: 'OEM-2024-008',
    oem: 'Bajaj Auto',
    plant: 'Bangalore',
    auditDate: '2024-03-12',
    auditType: 'New Part Approval Audit',
    auditor: 'Bajaj Engineering Team',
    score: 92,
    criticalNCR: 0,
    majorNCR: 2,
    minorNCR: 5,
    repeatIssues: 0,
    capaOpen: 4,
    capaClosed: 3,
    nextAudit: '2024-09-12',
    status: 'Completed',
    remarks: 'PPAP documentation complete, minor FMEA updates needed'
  }, {
    auditId: 'OEM-2024-009',
    oem: 'Hyundai',
    plant: 'Manesar',
    auditDate: '2024-03-18',
    auditType: 'Quarterly Review Audit',
    auditor: 'Hyundai Quality Team',
    score: 94,
    criticalNCR: 0,
    majorNCR: 1,
    minorNCR: 4,
    repeatIssues: 0,
    capaOpen: 3,
    capaClosed: 2,
    nextAudit: '2024-06-18',
    status: 'Completed',
    remarks: 'Consistent high performance maintained'
  }, {
    auditId: 'OEM-2024-010',
    oem: 'TVS Motor',
    plant: 'Chennai',
    auditDate: '2024-03-25',
    auditType: 'Process Capability Audit',
    auditor: 'TVS Process Engineering',
    score: 93,
    criticalNCR: 0,
    majorNCR: 2,
    minorNCR: 5,
    repeatIssues: 1,
    capaOpen: 4,
    capaClosed: 3,
    nextAudit: '2024-09-25',
    status: 'In Progress',
    remarks: 'Cpk values need improvement on 2 critical dimensions'
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'text-red-600 bg-red-50';
      case 'Major':
        return 'text-orange-600 bg-orange-50';
      case 'Minor':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Planned':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getScoreColor = (score: number) => {
    if (score >= 93) return '#10b981';
    if (score >= 90) return '#3b82f6';
    if (score >= 85) return '#f59e0b';
    return '#ef4444';
  };
  return <div data-id="element-1970">
      <DescriptionPanel title="External OEM Audits Performance" description="Comprehensive tracking of customer audit performance across major OEMs including Tata Motors, Bajaj Auto, TVS, Hyundai, and others. Monitor audit scores, findings severity, and recurring issues to maintain strong customer relationships and compliance." insights={['Hyundai audit score leading at 95/100 - best performance across all OEMs', 'Major non-conformances reduced to 18 from 24 last quarter - 25% improvement', 'Repeat issues down to 12% from 17% - systematic improvements working', 'Overall OEM audit score improved to 92/100 with consistent upward trend']} data-id="element-1971" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-1972">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1973">
          OEM Audit Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1974">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-1975">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1976">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-1977">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-1978">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1979">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-1980" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-1981" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* OEM-wise Audit Scores and Severity Heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1982">
        {/* OEM-wise Audit Scores */}
        <Card className="p-6" data-id="element-1983">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1984">
            OEM-wise Audit Scores
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1985">
            Latest audit scores by OEM customer
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1986">
            <BarChart data={oemAuditScoresData} layout="vertical" data-id="element-1987">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1988" />
              <XAxis type="number" domain={[80, 100]} tick={{
              fontSize: 11
            }} data-id="element-1989" />
              <YAxis dataKey="oem" type="category" width={120} tick={{
              fontSize: 11
            }} data-id="element-1990" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1991" />
              <Legend data-id="element-1992" />
              <Bar dataKey="latest" name="Latest Score" data-id="element-1993">
                {oemAuditScoresData.map((entry, index) => <Cell key={`cell-${index}`} fill={getScoreColor(entry.latest)} data-id="element-1994" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1995">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-1996">
              <div className="text-center" data-id="element-1997">
                <div className="text-gray-600" data-id="element-1998">Highest Score</div>
                <div className="text-lg font-bold text-green-600" data-id="element-1999">
                  Hyundai (95)
                </div>
              </div>
              <div className="text-center" data-id="element-2000">
                <div className="text-gray-600" data-id="element-2001">Avg Score</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-2002">92.7</div>
              </div>
              <div className="text-center" data-id="element-2003">
                <div className="text-gray-600" data-id="element-2004">Total OEMs</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-2005">6</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Severity Heatmap */}
        <Card className="p-6" data-id="element-2006">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2007">
            Findings Severity Heatmap
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-2008">
            Non-conformances by category and severity level
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-2009">
            <BarChart data={findingsSeverityData} data-id="element-2010">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2011" />
              <XAxis dataKey="category" tick={{
              fontSize: 10,
              angle: -45,
              textAnchor: 'end'
            }} height={100} data-id="element-2012" />
              <YAxis tick={{
              fontSize: 11
            }} data-id="element-2013" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-2014" />
              <Legend data-id="element-2015" />
              <Bar dataKey="critical" stackId="a" fill="#ef4444" name="Critical" data-id="element-2016" />
              <Bar dataKey="major" stackId="a" fill="#f59e0b" name="Major" data-id="element-2017" />
              <Bar dataKey="minor" stackId="a" fill="#fbbf24" name="Minor" data-id="element-2018" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2019">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-2020">
              <div className="text-center" data-id="element-2021">
                <div className="text-gray-600" data-id="element-2022">Critical</div>
                <div className="text-lg font-bold text-red-600" data-id="element-2023">4</div>
              </div>
              <div className="text-center" data-id="element-2024">
                <div className="text-gray-600" data-id="element-2025">Major</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-2026">24</div>
              </div>
              <div className="text-center" data-id="element-2027">
                <div className="text-gray-600" data-id="element-2028">Minor</div>
                <div className="text-lg font-bold text-yellow-600" data-id="element-2029">61</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Issues Recurrence Trend */}
      <Card className="p-6 mb-8" data-id="element-2030">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2031">
          Issues Recurrence Trend
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-2032">
          Track recurring issues to identify systematic problems requiring root
          cause analysis
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-2033">
          <ComposedChart data={issuesRecurrenceData} data-id="element-2034">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2035" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-2036" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} data-id="element-2037" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 20]} tick={{
            fontSize: 12
          }} data-id="element-2038" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-2039" />
            <Legend data-id="element-2040" />
            <Bar yAxisId="left" dataKey="totalIssues" fill="#3b82f6" name="Total Issues" data-id="element-2041" />
            <Bar yAxisId="left" dataKey="repeatIssues" fill="#ef4444" name="Repeat Issues" data-id="element-2042" />
            <Line yAxisId="right" type="monotone" dataKey="repeatPercent" stroke="#10b981" strokeWidth={3} name="Repeat %" dot={{
            r: 5
          }} data-id="element-2043" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2044">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-2045">
            <div className="text-center" data-id="element-2046">
              <div className="text-gray-600" data-id="element-2047">Current Month</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2048">26 issues</div>
              <div className="text-xs text-gray-500" data-id="element-2049">total findings</div>
            </div>
            <div className="text-center" data-id="element-2050">
              <div className="text-gray-600" data-id="element-2051">Repeat Issues</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2052">3 issues</div>
              <div className="text-xs text-gray-500" data-id="element-2053">recurring</div>
            </div>
            <div className="text-center" data-id="element-2054">
              <div className="text-gray-600" data-id="element-2055">Repeat Rate</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-2056">11.5%</div>
              <div className="text-xs text-gray-500" data-id="element-2057">current</div>
            </div>
            <div className="text-center" data-id="element-2058">
              <div className="text-gray-600" data-id="element-2059">Improvement</div>
              <div className="text-xl font-bold text-green-600" data-id="element-2060">-6.3%</div>
              <div className="text-xs text-gray-500" data-id="element-2061">vs Jan</div>
            </div>
          </div>
        </div>
      </Card>
      {/* OEM Audit Details Table */}
      <Card className="p-6" data-id="element-2062">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2063">
          OEM Audit Details
        </h3>
        <div className="overflow-x-auto" data-id="element-2064">
          <table className="w-full" data-id="element-2065">
            <thead data-id="element-2066">
              <tr className="border-b-2 border-gray-300" data-id="element-2067">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2068">
                  Audit ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2069">
                  OEM
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2070">
                  Plant
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2071">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2072">
                  Audit Type
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2073">
                  Auditor
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2074">
                  Score
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2075">
                  Critical
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2076">
                  Major
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2077">
                  Minor
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2078">
                  Repeat
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2079">
                  CAPA Open
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2080">
                  CAPA Closed
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2081">
                  Next Audit
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2082">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2083">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2084">
              {oemAuditDetailsData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-2085">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-2086">
                    {row.auditId}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-2087">
                    {row.oem}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2088">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2089">
                    {row.auditDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2090">
                    {row.auditType}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2091">
                    {row.auditor}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2092">
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold" style={{
                  backgroundColor: row.score >= 93 ? '#d1fae5' : row.score >= 90 ? '#dbeafe' : row.score >= 85 ? '#fef3c7' : '#fee2e2',
                  color: row.score >= 93 ? '#065f46' : row.score >= 90 ? '#1e40af' : row.score >= 85 ? '#92400e' : '#991b1b'
                }} data-id="element-2093">
                      {row.score}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2094">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${row.criticalNCR > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-400'}`} data-id="element-2095">
                      {row.criticalNCR}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2096">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${row.majorNCR > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`} data-id="element-2097">
                      {row.majorNCR}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2098">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${row.minorNCR > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-400'}`} data-id="element-2099">
                      {row.minorNCR}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2100">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${row.repeatIssues > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-id="element-2101">
                      {row.repeatIssues}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-2102">
                    {row.capaOpen}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-green-600 font-semibold" data-id="element-2103">
                    {row.capaClosed}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2104">
                    {row.nextAudit}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2105">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-2106">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2107">
                    {row.remarks}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-2108">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-2109">
            <div className="text-center" data-id="element-2110">
              <div className="text-gray-600" data-id="element-2111">Total Audits</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-2112">10</div>
              <div className="text-xs text-gray-500" data-id="element-2113">completed</div>
            </div>
            <div className="text-center" data-id="element-2114">
              <div className="text-gray-600" data-id="element-2115">Avg Score</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2116">92.6</div>
              <div className="text-xs text-gray-500" data-id="element-2117">overall</div>
            </div>
            <div className="text-center" data-id="element-2118">
              <div className="text-gray-600" data-id="element-2119">Critical NCRs</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2120">1</div>
              <div className="text-xs text-gray-500" data-id="element-2121">total</div>
            </div>
            <div className="text-center" data-id="element-2122">
              <div className="text-gray-600" data-id="element-2123">Major NCRs</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-2124">18</div>
              <div className="text-xs text-gray-500" data-id="element-2125">total</div>
            </div>
            <div className="text-center" data-id="element-2126">
              <div className="text-gray-600" data-id="element-2127">Repeat Issues</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2128">7</div>
              <div className="text-xs text-gray-500" data-id="element-2129">recurring</div>
            </div>
            <div className="text-center" data-id="element-2130">
              <div className="text-gray-600" data-id="element-2131">CAPA Closure</div>
              <div className="text-xl font-bold text-green-600" data-id="element-2132">76%</div>
              <div className="text-xs text-gray-500" data-id="element-2133">closure rate</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ExternalOEMAuditsDashboard;